import React, { createContext, useContext, useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { setAuthToken } from '../lib/api'
import { trackLogin, trackLoginFailed, trackSignUp, AuthMethod } from '../lib/analyticsEvents'
import { clearSavedQuizSession } from '../hooks/useQuizSession'
import { getAcquisition, stashForOAuthHop } from '../lib/acquisition'

interface AuthContextValue {
  session: Session | null
  loading: boolean
  error: string | null
  recoveryMode: boolean
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<{ error: string | null; needsConfirmation: boolean }>
  signInWithGoogle: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: string | null }>
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recoveryMode, setRecoveryMode] = useState(false)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthToken(data.session?.access_token ?? null)
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Fired when the user opens a reset link — gates /reset-password until the password is set.
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true)
      if (event === 'SIGNED_OUT') clearSavedQuizSession()
      setSession(session)
      setAuthToken(session?.access_token ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  async function signInWithEmail(email: string, password: string) {
    setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.message)
      trackLoginFailed(AuthMethod.PASSWORD, error.message)
    } else {
      trackLogin(AuthMethod.PASSWORD)
    }
  }

  async function signUpWithEmail(email: string, password: string) {
    setError(null)
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      // Must match mobile's EMAIL_LINK_REDIRECT_URL; the bridge page works on desktop too.
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirmed`,
        ...(getAcquisition() ? { data: { acquisition: getAcquisition() } } : {}),
      },
    })
    if (error) {
      setError(error.message)
      return { error: error.message, needsConfirmation: false }
    }
    // Supabase returns an empty `identities` array for an already-confirmed email, with no error.
    if (data.user && data.user.identities?.length === 0) {
      const message = 'An account with this email already exists. Try signing in instead.'
      setError(message)
      return { error: message, needsConfirmation: false }
    }
    // Fire on account creation, not on session: with confirmations on, signUp returns no session.
    trackSignUp(AuthMethod.PASSWORD)
    return { error: null, needsConfirmation: !data.session }
  }

  async function signInWithGoogle() {
    setError(null)
    stashForOAuthHop()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    // Only redirect-initiation errors surface here; success is tracked at /auth/callback.
    if (error) {
      setError(error.message)
      trackLoginFailed(AuthMethod.GOOGLE, error.message)
    }
  }

  async function resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error: error?.message ?? null }
  }

  async function updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (!error) setRecoveryMode(false)
    return { error: error?.message ?? null }
  }

  async function signOut() {
    clearSavedQuizSession()
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ session, loading, error, recoveryMode, signInWithEmail, signUpWithEmail, signInWithGoogle, resetPassword, updatePassword, signOut, clearError: () => setError(null) }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
